/**
 * BeachesWidget (spec 015 · T069).
 *
 * The "For you right now" launcher widget panel for SurfTime.
 *
 * Faithful to `tmp/platform-mockups/launcher.html`:
 *   - Panel card with title "Your beaches" + mono eyebrow "surftime · liked spots"
 *   - One row per liked beach:
 *       [heart] beach name (italic Fraunces)  |  wave height "5.2ft"  |  quality pill
 *       small: break_type · wind summary
 *   - Quality pill uses a [data-quality] attribute so color mapping
 *     (poor / fair / good / epic → gray / amber / blue / magenta) stays
 *     in CSS, driven by launcher-home.css.
 *
 * Props shape matches `LauncherLikedBeach` from
 * `frontend/src/services/launcher.ts` — direct projection of
 * `FavoriteResponse` from the backend. When the list is empty we render
 * the quiet empty state with an "Open SurfTime" CTA.
 */
import { Heart, Waves } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { LauncherLikedBeach } from '@/services/launcher';
import { REGION_LABELS as CANONICAL_REGION_LABELS } from '@/lib/regionLabels';

interface BeachesWidgetProps {
  beaches: LauncherLikedBeach[];
}

/**
 * F-04b-class fix (found by the PWA lane in its own tree, mirrored here):
 * the pill used to render `quality` RAW — a `fair_to_good` beach showed
 * "fair_to_good" verbatim. Kept as its own local copy (not imported from
 * `apps/surftime/components/QualityBadge`) since this widget lives at the
 * platform-launcher layer, outside any single app's internal component
 * tree — same "each surface formats its own band word" convention
 * `bandAxis.tsx`'s header documents for the SurfTime app itself.
 * `data-quality` (the CSS color-mapping hook, `launcher-widgets.css`)
 * still carries the raw value unchanged — only the visible text changes.
 */
const BAND_LABELS: Record<string, string> = {
  no_data: 'No data',
  poor: 'Poor',
  fair: 'Fair',
  good: 'Good',
  epic: 'Epic',
};

/**
 * #77 follow-up (021 fix-wave): this used to be a private copy of the SAME
 * 8-entry table `apps/surftime/utils/region.ts` (now `lib/regionLabels.ts`)
 * owns, with no cross-reference — 7 of 8 entries had drifted into
 * byte-for-byte duplicates with nothing marking the relationship. Now
 * composed from the canonical map, overriding only the one entry this
 * widget deliberately renders shorter than the app does — its row has far
 * less horizontal room than a beach-detail page, so "Ventura / Santa
 * Barbara" would truncate or wrap where "Ventura · SB" fits on one line.
 */
const REGION_LABELS: Record<string, string> = {
  ...CANONICAL_REGION_LABELS,
  ventura_santa_barbara: 'Ventura · SB',
};

function formatWaveHeight(ft: number | null | undefined): string {
  if (ft === null || ft === undefined || Number.isNaN(ft)) return '—';
  return ft >= 10 ? ft.toFixed(0) : ft.toFixed(1);
}

function windSummary(mph: number | null | undefined): string {
  if (mph === null || mph === undefined || Number.isNaN(mph)) return 'wind —';
  const rounded = Math.round(mph);
  return `${rounded} mph`;
}

function BeachRow({ beach }: { beach: LauncherLikedBeach }) {
  const cond = beach.current_conditions;
  // Honest absence, not a fabricated 'poor' — a beach with no current
  // reading isn't the same as one with a real bad reading (T158).
  const quality = cond?.quality_rating ?? 'no_data';
  const region = beach.region ? (REGION_LABELS[beach.region] ?? beach.region) : '';
  const waveDisplay = formatWaveHeight(cond?.wave_height_ft ?? null);

  return (
    <Link
      to="/apps/surftime"
      className="beach-row"
      data-testid={`beach-row-${beach.slug}`}
    >
      <div className="beach-row__info">
        <Heart className="beach-row__heart" aria-hidden="true" />
        <div className="beach-row__name">
          {beach.name}
          <small>
            {region}
            {region && cond ? ' · ' : ''}
            {cond ? windSummary(cond.wind_speed_mph) : 'no reading'}
          </small>
        </div>
      </div>
      <div className="beach-row__wave">
        {waveDisplay}
        <small>ft</small>
      </div>
      <div className="quality-pill" data-quality={quality}>
        {BAND_LABELS[quality] ?? quality}
      </div>
    </Link>
  );
}

function BeachesEmpty() {
  return (
    <div className="widget__empty">
      <p className="widget__empty-line">No beaches liked yet.</p>
      <Link to="/apps/surftime" className="widget__empty-cta">
        Open SurfTime to like beaches →
      </Link>
    </div>
  );
}

export default function BeachesWidget({ beaches }: BeachesWidgetProps) {
  return (
    <section
      className="widget widget--beaches"
      aria-labelledby="widget-beaches-title"
    >
      <header className="widget__head">
        <span className="widget__logo" aria-hidden="true">
          <Waves />
        </span>
        <div className="widget__title-block">
          <h3 id="widget-beaches-title" className="widget__title">
            Your beaches
          </h3>
          <div className="widget__sub">surftime · liked spots</div>
        </div>
        <Link to="/apps/surftime" className="widget__link">
          All →
        </Link>
      </header>
      <div className="widget__body">
        {beaches.length === 0 ? (
          <BeachesEmpty />
        ) : (
          beaches.map((b) => <BeachRow key={b.id} beach={b} />)
        )}
      </div>
    </section>
  );
}
